// Phase 735.30 — Global in-app PDF viewer.
//
// WHY THIS EXISTS
// ---------------
// The previous "open PDF in a new tab" implementation (`<a target="_blank">`)
// was intermittently blocked by browser extensions and Chrome popup rules
// even though the underlying fetch to the API worked (confirmed via
// Playwright same-origin fetch returning 200 for every PDF endpoint —
// meetings, approval-report and cash-ledger — in a clean browser context).
//
// Symptoms in affected browsers: `ERR_BLOCKED_BY_CLIENT` on the navigation
// URL. Fetch to the same URL still succeeds.
//
// This component mounts ONCE at the app root and listens for a custom
// `emergent:open-pdf` event. When dispatched, it fetches the PDF as a Blob
// (same-origin, cookie auth), creates a blob URL and displays it in an
// iframe INSIDE the current page. Extensions cannot pattern-match a `blob:`
// URL, and no new-tab navigation is performed.

import React, { useEffect, useState, useRef } from 'react';
import { X, DownloadSimple, Printer, ArrowSquareOut } from '@phosphor-icons/react';

export default function PdfViewerHost() {
  const [state, setState] = useState({
    open: false,
    url: null,        // blob: URL for iframe
    directUrl: null,  // real HTTP URL (for "open in new tab" fallback link)
    filename: 'document.pdf',
    loading: false,
    error: null,
  });
  const iframeRef = useRef(null);

  useEffect(() => {
    const handler = async (e) => {
      const detail = e.detail || {};
      const httpUrl = detail.url;
      const filename = detail.filename || 'document.pdf';
      // Phase 735.49 — allow callers to hand in a pre-fetched Blob (used
      // for POST-based bulk PDF endpoints where the URL alone cannot
      // encode all filters).
      if (detail.blob instanceof Blob) {
        try {
          const raw = detail.blob;
          const blob = raw.type === 'application/pdf' ? raw : new Blob([raw], { type: 'application/pdf' });
          const objUrl = URL.createObjectURL(blob);
          setState({ open: true, url: objUrl, directUrl: null, filename, loading: false, error: null });
        } catch (err) {
          console.error('[PdfViewerHost] blob open failed', err);
          setState({ open: true, url: null, directUrl: null, filename, loading: false, error: err.message || String(err) });
        }
        return;
      }
      setState({ open: true, url: null, directUrl: httpUrl, filename, loading: true, error: null });
      try {
        const res = await fetch(httpUrl, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/pdf' },
          cache: 'no-store',
        });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const errBody = await res.clone().json();
            if (errBody?.detail) msg = typeof errBody.detail === 'string' ? errBody.detail : msg;
          } catch (_e) { /* body not JSON */ }
          throw new Error(msg);
        }
        const raw = await res.blob();
        const blob = raw.type === 'application/pdf'
          ? raw
          : new Blob([raw], { type: 'application/pdf' });
        const objUrl = URL.createObjectURL(blob);
        setState({ open: true, url: objUrl, directUrl: httpUrl, filename, loading: false, error: null });
      } catch (err) {
        console.error('[PdfViewerHost] fetch failed', err);
        setState({ open: true, url: null, directUrl: httpUrl, filename, loading: false, error: err.message || String(err) });
      }
    };
    window.addEventListener('emergent:open-pdf', handler);
    return () => window.removeEventListener('emergent:open-pdf', handler);
  }, []);

  const close = () => {
    if (state.url) {
      try { URL.revokeObjectURL(state.url); } catch (_e) { /* ignore */ }
    }
    setState({ open: false, url: null, directUrl: null, filename: 'document.pdf', loading: false, error: null });
  };

  const doPrint = () => {
    try {
      iframeRef.current?.contentWindow?.focus();
      iframeRef.current?.contentWindow?.print();
    } catch (err) {
      console.warn('[PdfViewerHost] iframe print blocked', err);
    }
  };

  if (!state.open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-2 sm:p-4"
      onClick={close}
      data-testid="pdf-viewer-modal"
    >
      <div
        className="bg-white w-full max-w-6xl h-full max-h-[96vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 gap-2">
          <span className="text-sm font-semibold truncate" title={state.filename}>{state.filename}</span>
          <div className="flex items-center gap-1 shrink-0">
            {state.url && (
              <a
                href={state.url}
                download={state.filename}
                className="p-1.5 rounded-none hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 inline-flex items-center"
                title="Download"
                data-testid="pdf-viewer-download"
              >
                <DownloadSimple size={16} />
              </a>
            )}
            {state.url && (
              <button
                type="button"
                onClick={doPrint}
                className="p-1.5 rounded-none hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900"
                title="Print"
                data-testid="pdf-viewer-print"
              >
                <Printer size={16} />
              </button>
            )}
            {state.url && (
              <a
                href={state.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-none hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 inline-flex items-center"
                title="Open in new tab"
                data-testid="pdf-viewer-open-new-tab"
              >
                <ArrowSquareOut size={16} />
              </a>
            )}
            <button
              type="button"
              onClick={close}
              className="p-1.5 rounded-none hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900"
              title="Close"
              data-testid="pdf-viewer-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-zinc-100 relative">
          {state.loading && (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm">
              Loading…
            </div>
          )}
          {state.error && !state.loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-red-700 text-sm gap-2 p-4 text-center">
              <span className="font-semibold">Failed to load PDF</span>
              <span className="text-xs text-red-600 font-mono">{state.error}</span>
              {state.directUrl && (
                <a
                  href={state.directUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline text-blue-600 mt-1"
                >
                  Try opening the URL directly
                </a>
              )}
            </div>
          )}
          {state.url && !state.loading && !state.error && (
            <iframe
              ref={iframeRef}
              src={state.url}
              title={state.filename}
              className="w-full h-full border-0"
              data-testid="pdf-viewer-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}
